import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * LA COQUILLE DES PANNEAUX FLOTTANTS DU PLANNING — `floatp` de la maquette.
 *
 * POURQUOI ELLE EST DEVENUE UN COMPOSANT (lot 5.6).
 * Le lot 5.5 bis avait donné cette coquille au panneau Événements : en-tête
 * dégradé qui sert de poignée, sous-barre, zone défilante, coin bas-droit qui
 * redimensionne. L'écran d'export en demande trois de plus (Colonnes, Types ×
 * colonnes, Événements × colonnes). Recopier le déplacement quatre fois, c'est
 * quatre pièges à réapprendre — dont celui de la taille initiale, ci-dessous,
 * qui n'a rien d'évident.
 *
 * Le composant ne connaît que la COQUILLE : le contenu, la sous-barre et le pied
 * lui sont passés. C'est le même partage que `FenetreDetachee` au lot 5.5 bis.
 *
 * Props :
 *   ouvert       : bool — fermé, le panneau n'est pas monté du tout
 *   titre, icone : l'en-tête (icône FontAwesome, sans emoji — règle 3)
 *   entete       : nœuds ajoutés dans l'en-tête, avant la poignée (compteurs…)
 *   sousBarre    : nœuds de la bande fixe sous l'en-tête, ou rien
 *   pied         : nœuds de la bande fixe du bas (« Terminé »), ou rien
 *   largeur / hauteur : dimensions D'OUVERTURE (l'utilisateur les change ensuite)
 *   ancre        : 'centre' (maquette) ou 'droite' (panneau Événements)
 *   hautInitial  : distance au haut de la fenêtre, en pixels
 *   onFermer     : la croix de l'en-tête
 */
function PanneauFlottant({
    ouvert,
    titre,
    icone = 'fa-window-restore',
    entete,
    sousBarre,
    pied,
    children,
    largeur = 520,
    hauteur = '58vh',
    ancre = 'centre',
    hautInitial = 90,
    onFermer,
    classeContenu = 'pl-floatp-scroll',
    // Le panneau Événements a besoin de MESURER sa propre boîte : le fantôme de
    // glisser-déposer change d'aspect selon qu'il est dedans ou dehors.
    refExterne
}) {
    const panneauRef = useRef(null);
    const poser = (n) => {
        panneauRef.current = n;
        if (refExterne) refExterne.current = n;
    };
    const [position, setPosition] = useState(null);
    const [glissement, setGlissement] = useState(null);

    const positionDOuverture = useCallback(() => {
        const l = typeof largeur === 'number' ? largeur : 520;
        const x = ancre === 'droite'
            ? Math.max(8, window.innerWidth - l - 20)
            : Math.max(8, Math.round((window.innerWidth - l) / 2));
        return { x, y: hautInitial };
    }, [largeur, ancre, hautInitial]);

    // La position n'est calculée qu'à la PREMIÈRE ouverture : rouvrir un panneau
    // qu'on avait déplacé doit le retrouver là où on l'avait laissé.
    useEffect(() => {
        if (!ouvert) return;
        setPosition(p => p || positionDOuverture());
    }, [ouvert, positionDOuverture]);

    // ⚠️ TAILLE INITIALE POSÉE SUR LE NŒUD, PAS DANS LE `style` DU JSX.
    // Le panneau se redimensionne par sa poignée native (`resize: both`), qui
    // écrit `width` et `height` dans l'attribut `style` de l'élément. Si React
    // gérait ces deux propriétés, le rendu suivant — une frappe au clavier dans
    // un champ du panneau suffit — les remettrait à leur valeur d'origine, et le
    // panneau reprendrait sa taille sous les doigts de l'utilisateur. React ne
    // touche qu'aux clés qu'il connaît : il ne voit ici que `left` et `top`.
    useEffect(() => {
        const n = panneauRef.current;
        if (!ouvert || !n || n.style.width) return;
        n.style.width = typeof largeur === 'number' ? largeur + 'px' : largeur;
        n.style.height = typeof hauteur === 'number' ? hauteur + 'px' : hauteur;
    }, [ouvert, largeur, hauteur]);

    // L'en-tête est la poignée (`panelDrag` de la maquette) — mais pas ce qu'on y
    // clique pour agir : la croix ne doit pas déplacer le panneau.
    const commencerGlissement = (e) => {
        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
        const rect = panneauRef.current.getBoundingClientRect();
        setGlissement({ dx: e.clientX - rect.left, dy: e.clientY - rect.top, largeur: rect.width });
    };

    useEffect(() => {
        if (!glissement) return;
        const bouger = (e) => setPosition({
            // On garde toujours 80 px du panneau à l'écran : déplacé trop loin, il
            // ne serait plus rattrapable à la souris.
            x: Math.max(80 - glissement.largeur, Math.min(window.innerWidth - 80, e.clientX - glissement.dx)),
            y: Math.max(0, Math.min(window.innerHeight - 40, e.clientY - glissement.dy))
        });
        const relacher = () => setGlissement(null);
        window.addEventListener('mousemove', bouger);
        window.addEventListener('mouseup', relacher);
        return () => {
            window.removeEventListener('mousemove', bouger);
            window.removeEventListener('mouseup', relacher);
        };
    }, [glissement]);

    // Une fenêtre rétrécie ne doit pas emporter le panneau hors de vue.
    useEffect(() => {
        if (!ouvert) return undefined;
        const surRedimensionnement = () => setPosition(p => p && ({
            x: Math.min(p.x, Math.max(8, window.innerWidth - 120)),
            y: Math.min(p.y, Math.max(8, window.innerHeight - 60))
        }));
        window.addEventListener('resize', surRedimensionnement);
        return () => window.removeEventListener('resize', surRedimensionnement);
    }, [ouvert]);

    if (!ouvert || !position) return null;

    return (
        <div
            ref={poser}
            className="pl-floatp"
            style={{ left: position.x + 'px', top: position.y + 'px' }}
        >
            <div className="pl-floatp-h" onMouseDown={commencerGlissement}>
                <i className={'fa-solid ' + icone} />
                <b>{titre}</b>
                {entete}
                <span className="pl-grow" />
                {/* Repère de la maquette : il dit que l'en-tête se glisse et que le
                    coin se tire. Une poignée qu'on ne devine pas n'existe pas. */}
                <i
                    className="fa-solid fa-up-down-left-right pl-floatp-poignee"
                    title="Glisser l'en-tête pour déplacer · coin bas-droit pour redimensionner"
                />
                <button className="pl-floatp-x" onClick={onFermer} aria-label="Fermer" title="Fermer">
                    <i className="fa-solid fa-xmark" />
                </button>
            </div>

            {sousBarre && <div className="pl-floatp-sub">{sousBarre}</div>}
            <div className={classeContenu}>{children}</div>
            {pied && <div className="pl-floatp-f">{pied}</div>}
        </div>
    );
}

export default PanneauFlottant;
